/**
 * ReportShell — consultancy-report cover + single linear column (spec §4.1).
 * Fully linear (no sidebars, no sticky columns) so PDF export prints faithfully.
 * KPIs are NOT chrome: they arrive as a kpi_grid block inside `sections`.
 *
 * The cover is deliberately restrained (ink-on-paper register): a hairline
 * gradient rule, a small-caps brand kicker, the title in the Fraunces display
 * face, and metadata as a labelled definition row. No loud gradient slabs.
 */
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface ReportMetadata {
  [key: string]: string | undefined
}

export interface ReportShellProps {
  title: string
  metadata: ReportMetadata
  sections: ReactNode
  className?: string
}

/** Turn a metadata key ("method_name") into a display label ("Method Name"). */
function formatLabel(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

export function ReportShell({ title, metadata, sections, className }: ReportShellProps) {
  const metaEntries = Object.entries(metadata).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0,
  )

  return (
    <article className={cn('report-root mx-auto w-full max-w-4xl', className)}>
      <header className="relative overflow-hidden rounded-xl border border-border bg-card px-8 py-10 sm:px-10 sm:py-12">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-ecosemantic" aria-hidden="true" />
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--report-accent)]">
          EcoSemantic · Life Cycle Assessment
        </p>
        <h1 className="mt-4 text-balance font-[family-name:var(--report-font-display)] text-4xl font-semibold leading-[1.08] tracking-tight text-foreground">
          {title}
        </h1>
        {metaEntries.length > 0 && (
          <dl className="mt-6 flex flex-wrap gap-x-10 gap-y-3 border-t border-border pt-5">
            {metaEntries.map(([key, value]) => (
              <div key={key} className="flex flex-col gap-0.5">
                <dt className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {formatLabel(key)}
                </dt>
                <dd className="text-sm font-medium text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </header>
      <div className="mt-8 flex flex-col">{sections}</div>
    </article>
  )
}
